import React, { useCallback, useMemo, useState } from 'react';
import { Text, Link } from '@sitecore-jss/sitecore-jss-react';
import Slider, { Settings } from 'react-slick';
import { useWindowSize } from 'react-use';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { StoriesProps } from './fields.definitions';
import { StoriesModel } from './fields.model';
import './index.scss';

import CustomArrow from '../../react-only-components/CarouselArrow';
import StoriesCard from '../../react-only-components/StoriesCard';
import LazySection from '../../react-only-components/LazySection';

const Stories = (props: StoriesProps) => {
  const { width } = useWindowSize()

  const isEE = props.sitecoreContext?.pageEditing
  const model = new StoriesModel(props)
  const lang = props.sitecoreContext?.language?.slice(0, 2)

  const slidesAmount = model.cards.length
  const isSliderNeeded =
    width > 1350
      ? slidesAmount > 3
      : width > 768
      ? slidesAmount > 2
      : slidesAmount > 1

  const [currentPage, setCurrentPage] = useState(1)

  const getProgress: () => { width: string } = useCallback(
    () => ({
      width:
        (currentPage / (slidesAmount - (width > 1350 ? 2 : 1))) * 100 + '%',
    }),
    [currentPage, slidesAmount, props, width]
  )
  const onAfterChange: (newIndex: number) => void = useCallback(
    (newIndex) => setCurrentPage(newIndex + 1),
    [props]
  )
  const getIsDisabled: (direction: 'next' | 'prev') => boolean = useCallback(
    (direction) =>
      direction === 'next'
        ? currentPage > slidesAmount - (width > 1350 ? 3 : 2)
        : currentPage === 1,
    [currentPage, width]
  )

  const settings: Settings = {
    variableWidth: true,
    slidesToScroll: 1,
    slidesToShow: 3,
    initialSlide: 0,
    dots: false,
    infinite: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1350,
        settings: {
          slidesToShow: 2,
        },
      },
    ],
    afterChange: onAfterChange,
    nextArrow: isSliderNeeded ? (<CustomArrow direction="next" isDisabled={getIsDisabled('next')} />) : <></>,
    prevArrow: isSliderNeeded ? (<CustomArrow direction="previous" isDisabled={getIsDisabled('prev')} />) : <></>,
  }

  const storiesMapper: (model: StoriesModel) => JSX.Element[] = useMemo(() => {
    return function Component(model: StoriesModel) {
      return model.cards.map((item, i) => {
        return (
          <div key={i} className="stories__card-container">
            <StoriesCard {...item} lang={lang} />
          </div>
        )
      })
    }
  }, [])

  return (
    <LazySection>
      {props.fields && (
        <div className="stories-wrapper" id={props?.rendering?.uid}>
          <div className="stories">
            <div className="stories__text">
              <Text
                tag="p"
                field={model.eyebrow}
                className="stories__eyebrow"
                editable={true}
              />
              <Text
                tag="h3"
                field={model.title}
                className="stories__title"
                editable={true}
              />
              {model.content && (
                <Text
                  tag="p"
                  field={model.content}
                  className="stories__content"
                  editable={true}
                />
              )}
              {(isEE || model.link?.value?.text || model.link?.value?.href) &&
                !model.redbutton && (
                  <div className={'stories__link'}>
                    <Link field={model.link} editable={true} />
                  </div>
                )}
              {model.redbutton && (
                <div className={'card-module__cta cta cta-primary-red'}>
                  <Link field={model.link} editable={true} />
                </div>
              )}
            </div>

            {isSliderNeeded ? (
              <div
                style={{
                  position: 'relative',
                  height: 'fit-content',
                  overflow: 'hidden',
                  flex: 1,
                }}
              >
                <Slider {...settings}>{storiesMapper(model)}</Slider>

                <div className="progress">
                  <div className="progress-label" style={getProgress()}></div>
                </div>
              </div>
            ) : (
              <div className="stories__container-no-slider">
                {storiesMapper(model)}
              </div>
            )}
          </div>
        </div>
      )}
    </LazySection>
  )
}

export default withSitecoreContext()(Stories)
